import React, { useEffect, useState, useCallback } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import api from "../../utils/api";
import ClassroomInfoCard from "./components/ClassroomInfoCard";
import StudentMatrixCard from "./components/StudentMatrixCard";
import StudentsListCard from "./components/StudentsListCard";
import AssignmentsCard from "./components/AssignmentsCard";
import WorkspaceCard from "./components/WorkspaceCard";

const PER_PAGE = 10;

function paginationFromResponse(res) {
    const p = res.data?.meta?.pagination;
    return {
        page: p?.page ?? 1,
        perPage: p?.perPage ?? PER_PAGE,
        total: p?.total ?? 0,
        totalPages: p?.totalPages ?? 0,
    };
}

export default function ClassroomDetails() {
    const { id } = useParams();

    const [loading, setLoading] = useState(true);
    const [classroom, setClassroom] = useState(null);
    const [matrix, setMatrix] = useState(null);
    const [assignments, setAssignments] = useState([]);
    const [workspace, setWorkspace] = useState({ notes: [], uploads: [] });

    const [students, setStudents] = useState([]);
    const [studentsPage, setStudentsPage] = useState(1);
    const [studentsTotal, setStudentsTotal] = useState(0);

    const loadClassroom = useCallback(async () => {
        const res = await api.get(`admin/classrooms/${id}`);
        setClassroom(res.data?.data ?? null);
    }, [id]);

    const loadStudents = useCallback(async () => {
        const res = await api.get(`admin/classrooms/${id}/students`, {
            params: { page: studentsPage, perPage: PER_PAGE },
        });
        setStudents(res.data?.data ?? []);
        const meta = paginationFromResponse(res);
        setStudentsTotal(meta.total);
    }, [id, studentsPage]);

    const loadMatrix = useCallback(async () => {
        const res = await api.get(`admin/classrooms/${id}/matrix`);
        setMatrix(res.data?.data ?? null);
    }, [id]);

    const loadAssignments = useCallback(async () => {
        const res = await api.get(`admin/classrooms/${id}/assignments`);
        setAssignments(res.data?.data ?? []);
    }, [id]);

    const loadWorkspace = useCallback(async () => {
        const res = await api.get(`admin/classrooms/${id}/workspace`);
        setWorkspace(res.data?.data ?? { notes: [], uploads: [] });
    }, [id]);

    useEffect(() => {
        let mounted = true;
        (async () => {
            setLoading(true);
            try {
                await Promise.all([loadClassroom(), loadMatrix(), loadAssignments(), loadWorkspace()]);
            } catch (e) {
                if (mounted) setClassroom(null);
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, [loadClassroom, loadMatrix, loadAssignments, loadWorkspace]);

    useEffect(() => {
        if (!id) return;
        loadStudents().catch(() => setStudents([]));
    }, [id, loadStudents]);

    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <CircularProgress size={24} />
                <Typography sx={{ mt: 1 }}>Loading classroom…</Typography>
            </Box>
        );
    }

    if (!classroom) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>Classroom not found.</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2, maxWidth: 1600, mx: "auto" }}>
            <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
                Classroom details
            </Typography>

            <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "1fr" }, alignItems: "stretch" }}>
                <ClassroomInfoCard classroom={classroom} />
                <StudentMatrixCard matrix={matrix} />
                <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, alignItems: "stretch" }}>
                    <StudentsListCard
                        students={students}
                        total={studentsTotal}
                        page={studentsPage}
                        perPage={PER_PAGE}
                        onPageChange={setStudentsPage}
                    />
                    <WorkspaceCard notes={workspace.notes || []} uploads={workspace.uploads || []} />
                </Box>
                <AssignmentsCard assignments={assignments} />
            </Box>
        </Box>
    );
}
